import { ENTER } from '@ipfabric/ui-components/utils/keyboard/constants';
import type {
  AdvancedFilterTableColumnDefinition,
  Filters,
} from '@ipfabric/utils';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { array } from 'fp-ts';
import { useEffect, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import './AdvancedFilter.css';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import type { JSX } from 'react';
import { FilterGroup } from './FilterGroup';
import { formatGroupFilter } from './formatFilter';
import type { AdvancedFilterHook } from './useAdvancedFilter';
import { isFilterInvalid } from './validateRule';

interface Props<TRow> {
  advancedFilterHook: AdvancedFilterHook;
  columns: AdvancedFilterTableColumnDefinition<TRow>[];
  // Sets valid filter or undefined
  onFilterChange: (filter: Filters | undefined) => void;
  // Update of submit count is used to touch fields & display visual reference for errors
  // so it can be set manually or tied with formik.
  submitCount: number;
  onSubmit: () => void;
  disableColorOperators?: boolean;
  onPreviewClick?: () => void;
  previewDisabled?: boolean;
}

export type AdvancedFilterColumnRecord<TRow> = Record<
  string,
  AdvancedFilterTableColumnDefinition<TRow>
>;

export const AdvancedFilter = <TRow,>(props: Props<TRow>): JSX.Element => {
  const {
    filter,
    handleFilterChange,
    handleDeleteFilter,
    clipboard,
    copy,
    handleValidate,
  } = props.advancedFilterHook;
  const columnRecord = useMemo(
    () =>
      props.columns.reduce<AdvancedFilterColumnRecord<TRow>>((acc, column) => {
        acc[column.key] = column;

        return acc;
      }, {}),
    [props.columns],
  );

  const ruleContext = useRuleContext();

  const { advancedFilter: advancedFilterMsg } = useLocale();

  // biome-ignore lint/correctness/useExhaustiveDependencies: propagate filter change only
  useEffect(() => {
    const invalid = isFilterInvalid(filter);
    props.onFilterChange(
      invalid ? undefined : (formatGroupFilter(filter, ruleContext) ?? {}),
    );
  }, [filter]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: execute on submit
  useEffect(() => {
    handleValidate();
  }, [props.submitCount]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === ENTER) {
      props.onSubmit();
    }
  };

  const previewBtn = props.onPreviewClick && (
    <Button
      type='button'
      variant='secondary'
      icon='fa-eye'
      className='mt-2.5'
      disabled={props.previewDisabled}
      onClick={props.onPreviewClick}
    >
      {advancedFilterMsg.preview}
    </Button>
  );

  return (
    <div className='advanced-filter' onKeyDown={handleKeyDown}>
      <FilterGroup
        filter={filter}
        path={array.empty}
        onChange={handleFilterChange}
        onDelete={handleDeleteFilter}
        columns={columnRecord}
        clipboard={clipboard}
        copy={copy}
        disableColorOperators={props.disableColorOperators}
        previewBtn={previewBtn}
      />
    </div>
  );
};
